import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  Save,
  Settings,
  Home,
  Building2,
  FileText,
  Phone,
  Info,
  BookOpen,
  Smartphone,
  Wifi,
  MapPin,
  Share2,
  Calendar
} from 'lucide-react';
import { HUB_SEO_PAGES, HUB_SEO_DEFAULTS, HUB_PROPERTIES } from '../pages/guest-hub/hubConfig';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const sitePages = [
  { id: 'home', name: 'Home', icon: Home, path: '/' },
  { id: 'properties', name: 'Properties', icon: Building2, path: '/properties' },
  { id: 'about', name: 'About', icon: Info, path: '/about' },
  { id: 'blog', name: 'Blog', icon: FileText, path: '/blog' },
  { id: 'contact', name: 'Contact', icon: Phone, path: '/contact' },
  { id: 'house-rules', name: 'House Rules', icon: BookOpen, path: '/house-rules' },
];

const hubIcons = { hub: Smartphone, 'hub-wifi': Wifi, 'hub-guidebook': BookOpen, 'hub-local-guide': MapPin, 'hub-social': Share2, 'hub-book': Calendar };
const hubPages = HUB_SEO_PAGES.map((p) => ({ ...p, icon: hubIcons[p.id] }));
const shortName = (p) => HUB_PROPERTIES.find((h) => h.id === p.id)?.name || p.name;
const propertyPage = (p) => ({ id: `property-${p.id}`, name: shortName(p), icon: Building2, path: `/property/${p.id}`, propertyName: shortName(p) });
const isHubPage = (id) => id.startsWith('hub');

const PageTabs = ({ items, activePage, seoData, onSelect, prefix }) => (
  <div className="flex flex-wrap gap-2">
    {items.map((page) => {
      const Icon = page.icon;
      const hasData = seoData[page.id]?.meta_title;
      return (
        <Button
          key={page.id}
          data-testid={`${prefix}-tab-${page.id}`}
          variant={activePage === page.id ? 'default' : 'outline'}
          size="sm"
          onClick={() => onSelect(page.id)}
          className={`relative ${activePage === page.id ? 'bg-slate-900' : ''}`}
        >
          <Icon className="h-4 w-4 mr-2" />
          {page.name}
          {hasData && (
            <span className="absolute -top-1 -right-1 w-2 h-2 bg-green-500 rounded-full"></span>
          )}
        </Button>
      );
    })}
  </div>
);

const AdminSEO = () => {
  const { token } = useAuth();
  const [activePage, setActivePage] = useState('home');
  const [seoData, setSeoData] = useState({});
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    meta_title: '',
    meta_description: '',
    meta_keywords: '',
    og_image: '',
    no_index: false
  });

  const propertyPages = properties.map(propertyPage);
  const pages = [...sitePages, ...propertyPages, ...hubPages];
  const active = pages.find((p) => p.id === activePage);
  const defaultTitle = active?.propertyName
    ? `${active.propertyName} | Washington DC Vacation Rental | Capitol View Stays`
    : HUB_SEO_DEFAULTS[activePage]?.meta_title || `Capitol View Stays | ${active?.name || ''}`;
  const defaultDescription = active?.propertyName
    ? `Book ${active.propertyName} directly with Capitol View Stays for the best rates. Fully furnished Washington DC rental with fast Wi-Fi and self check-in.`
    : HUB_SEO_DEFAULTS[activePage]?.meta_description || 'Premium vacation rentals in Washington DC. Book directly for best rates...';

  useEffect(() => {
    fetchSEOSettings();
    fetch(`${API_URL}/api/public/properties`).then((r) => (r.ok ? r.json() : [])).then(setProperties).catch(() => {});
  }, []);

  useEffect(() => {
    const pageData = seoData[activePage] || {};
    setFormData({
      meta_title: pageData.meta_title || '',
      meta_description: pageData.meta_description || '',
      meta_keywords: pageData.meta_keywords || '',
      og_image: pageData.og_image || '',
      no_index: pageData.no_index ?? HUB_SEO_DEFAULTS[activePage]?.no_index ?? false
    });
  }, [activePage, seoData]);

  const fetchSEOSettings = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/seo`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        const seoMap = {};
        data.forEach(item => {
          seoMap[item.page] = item;
        });
        setSeoData(seoMap);
      }
    } catch (error) {
      console.error('Error fetching SEO settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/seo/${activePage}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        const updated = await response.json();
        setSeoData(prev => ({
          ...prev,
          [activePage]: updated
        }));
        alert('SEO settings saved successfully!');
      }
    } catch (error) {
      console.error('Error saving SEO settings:', error);
      alert('Failed to save SEO settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            SEO Settings
          </CardTitle>
          <CardDescription>
            Manage meta tags and SEO settings for each page
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Page Tabs */}
          <div className="space-y-4 mb-6 pb-6 border-b">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Website</p>
              <PageTabs items={sitePages} activePage={activePage} seoData={seoData} onSelect={setActivePage} prefix="seo" />
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Properties ({propertyPages.length})</p>
              <PageTabs items={propertyPages} activePage={activePage} seoData={seoData} onSelect={setActivePage} prefix="seo" />
              <p className="text-xs text-slate-500 mt-2">Each property detail page (/property/…) gets its own title, description and share image.</p>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Guest Hub (QR pages)</p>
              <PageTabs items={hubPages} activePage={activePage} seoData={seoData} onSelect={setActivePage} prefix="seo" />
              <p className="text-xs text-slate-500 mt-2">
                One setting covers all {propertyPages.length || 7} properties. Use <code className="bg-slate-100 px-1 rounded">{'{property}'}</code> anywhere and it becomes the property name (e.g. "Capitol Hill 1").
              </p>
            </div>
          </div>

          {/* SEO Form */}
          <div className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="meta_title">Meta Title</Label>
              <Input
                id="meta_title"
                data-testid="seo-meta-title-input"
                value={formData.meta_title}
                onChange={(e) => setFormData({ ...formData, meta_title: e.target.value })}
                placeholder={defaultTitle}
              />
              <p className="text-xs text-slate-500">
                Recommended: 50-60 characters. Current: {formData.meta_title.length}
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="meta_description">Meta Description</Label>
              <Textarea
                id="meta_description"
                data-testid="seo-meta-description-input"
                value={formData.meta_description}
                onChange={(e) => setFormData({ ...formData, meta_description: e.target.value })}
                placeholder={defaultDescription}
                rows={3}
              />
              <p className="text-xs text-slate-500">
                Recommended: 150-160 characters. Current: {formData.meta_description.length}
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="meta_keywords">Meta Keywords</Label>
              <Input
                id="meta_keywords"
                value={formData.meta_keywords}
                onChange={(e) => setFormData({ ...formData, meta_keywords: e.target.value })}
                placeholder="Washington DC vacation rental, Capitol Hill Airbnb, DC short term rental"
              />
              <p className="text-xs text-slate-500">
                Comma-separated keywords (less important for modern SEO, but still useful)
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="og_image">Open Graph Image URL</Label>
              <Input
                id="og_image"
                value={formData.og_image}
                onChange={(e) => setFormData({ ...formData, og_image: e.target.value })}
                placeholder="https://example.com/og-image.jpg"
              />
              <p className="text-xs text-slate-500">
                Image shown when page is shared on social media. Recommended: 1200x630px
              </p>
              {formData.og_image && (
                <div className="mt-2">
                  <img 
                    src={formData.og_image} 
                    alt="OG Preview" 
                    className="max-w-xs rounded-lg border"
                    onError={(e) => e.target.style.display = 'none'}
                  />
                </div>
              )}
            </div>

            <div className="flex items-start gap-3 rounded-lg border p-4 bg-slate-50">
              <input
                id="no_index"
                type="checkbox"
                data-testid="seo-no-index-checkbox"
                className="mt-1 h-4 w-4 accent-amber-500"
                checked={formData.no_index}
                onChange={(e) => setFormData({ ...formData, no_index: e.target.checked })}
              />
              <div>
                <Label htmlFor="no_index">Hide this page from Google (noindex)</Label>
                <p className="text-xs text-slate-500 mt-1">
                  {isHubPage(activePage)
                    ? 'Recommended ON for Wi-Fi and Guidebook pages so private guest info never shows in search results.'
                    : 'Leave OFF for normal website pages so they can rank in search.'}
                </p>
              </div>
            </div>

            <div className="pt-4 border-t">
              <Button 
                onClick={handleSave} 
                disabled={saving}
                data-testid="seo-save-button"
                className="bg-amber-500 hover:bg-amber-600 text-slate-900"
              >
                <Save className="h-4 w-4 mr-2" />
                {saving ? 'Saving...' : 'Save SEO Settings'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Preview Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Search Result Preview</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="bg-white border rounded-lg p-4 max-w-xl" data-testid="seo-preview">
            <div className="text-blue-600 text-lg hover:underline cursor-pointer truncate">
              {(formData.meta_title || defaultTitle).replace(/\{property\}/gi, 'Capitol Hill 1')}
            </div>
            <div className="text-green-700 text-sm truncate">
              www.capitol-view-stay.com{active?.path || ''}
            </div>
            <div className="text-slate-600 text-sm mt-1 line-clamp-2">
              {(formData.meta_description || defaultDescription).replace(/\{property\}/gi, 'Capitol Hill 1')}
            </div>
            {formData.no_index && (
              <p className="text-xs text-red-600 mt-2" data-testid="seo-preview-noindex">Hidden from search engines (noindex)</p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminSEO;
